import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { Form, Button as GrommetButton, ResponsiveContext } from 'grommet';
import { Add, Close } from 'grommet-icons';
import { CircleInformation } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { ApiError, ClientService, TCreateUserRequest, UserService } from '/src/api';
import {
  Box,
  BreadcrumbNav,
  BreadcrumbNavProps,
  ClientsSelect,
  FormCard,
  FormCardSection,
  FormPage,
  Input,
  Line,
  Select,
} from '/src/components';
import { config } from '/src/config';
import { useGlobalStore, useUserStore } from '/src/context';
import { ExternalRoles, RoleTypes, TClientId, TFacilityId, roleIdOptions } from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import { tooltipMessages } from '/src/lib/tooltips';
import { SelectOption, UserDataForm } from '/src/lib/types';
import { getFacilityLabel, getIsMobile, getPageTitle, getQueryParams, isValidEmail } from '/src/utils';

export const CreateUserPage = observer(() => {
  /** Context **/
  const userStore = useUserStore();
  const globalStore = useGlobalStore();
  const { search } = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const screenSize = useContext(ResponsiveContext);
  const queryParams = getQueryParams(search);
  const params = useParams();
  const clientId = useMemo(() => parseInt(queryParams.client_id ?? params.client_id), [queryParams, params]);
  const facilityId = useMemo(() => parseInt(queryParams.facility_id ?? params.facility_id), [queryParams, params]);
  const tooltips = tooltipMessages();
  const { t } = useTranslation();

  /** Refs **/
  const defaultFormData = useRef<UserDataForm>({
    name: '',
    email: '',
    phone: '',
    is_email_verified: true,
    role_id: NaN,
    client_ids: [],
    facility_ids: [],
    add_facility_ids: [],
    remove_facility_ids: [],
  });

  /** State **/
  const [isCreating, setIsCreating] = useState(false);
  const [isPhoneFocused, setIsPhoneFocused] = useState(false);
  const [breadcrumbNavItems, setBreadcrumbNavItems] = useState<BreadcrumbNavProps['previousPages']>([]);
  const [facilityOptions, setFacilityOptions] = useState<SelectOption<TFacilityId>[]>();
  const [showMultipleClients, setShowMultipleClients] = useState(false);

  const [formValues, setFormValues] = useState<UserDataForm>({
    ...defaultFormData.current,
    client_ids: clientId ? [clientId] : [],
    role_id: facilityId ? RoleTypes.FacilityAdmin : clientId ? RoleTypes.ClientAdmin : RoleTypes.FuseAdmin,
    facility_ids: facilityId ? [facilityId] : [],
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  /** Computed **/
  const isMobile = getIsMobile(screenSize);
  const isLoading = !(breadcrumbNavItems && facilityOptions);

  const filteredRoleIdOptions = useMemo(() => {
    // filter out Sales Rep role for the time being
    const options = roleIdOptions.filter((option) => option.value !== RoleTypes.FuseSalesRep);

    return userStore.isClientUser ? options.filter((option) => ExternalRoles.includes(option.value)) : options;
  }, [userStore.isClientUser, roleIdOptions]);

  /** Methods **/
  const updateFormValue = (key: keyof UserDataForm, value: unknown) => {
    if (key === 'role_id' && value === RoleTypes.FacilityAdmin && formValues.client_ids.length > 1) {
      setFormValues({
        ...formValues,
        [key]: value,
        client_ids: formValues.client_ids.slice(0, 1),
      });
      setShowMultipleClients(false);
    } else {
      setFormValues({
        ...formValues,
        [key]: value,
      });
    }

    if (formErrors[key]) {
      const updatedErrors = { ...formErrors };
      updatedErrors[key] = '';
      setFormErrors(updatedErrors);
    }
  };

  const formatPhoneNumber = (value: string) => {
    const [mainNumber, ...extensionParts] = value.toLowerCase().split(/(ext|x)/i);
    const extension = extensionParts.join('');

    if (mainNumber.match(/[a-zA-Z]/)) {
      const phoneMatch = mainNumber.match(/^\(\d{3}\) \d{3}-\d{4}/);
      return phoneMatch ? phoneMatch[0] + value.slice(phoneMatch[0].length) : value;
    }

    const digitsOnly = mainNumber.replace(/[^\d+]/g, '');

    if (!digitsOnly) return '';
    if (digitsOnly.startsWith('+')) return `+${digitsOnly.slice(1)}${extension}`;

    let formatted = '';
    if (digitsOnly.length >= 10) {
      formatted = `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
      if (digitsOnly.length > 10) formatted += ` ${digitsOnly.slice(10)}`;
      else if (mainNumber.endsWith(' ')) formatted += ' ';
    } else if (digitsOnly.length > 6) {
      formatted = `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
    } else if (digitsOnly.length > 3) {
      formatted = `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
    } else {
      formatted = `(${digitsOnly}`;
    }

    return formatted + extension;
  };

  const fetchPageData = async (clientId: TClientId) => {
    try {
      const client = userStore.isFacilityUser
        ? userStore.user?.clients.find((client) => client.id === clientId)
        : await ClientService.get({ id: clientId });

      const facilities = userStore.isFacilityUser ? userStore.user?.facilities : client?.facilities;

      if (!client || !facilities) return;

      setBreadcrumbNavItems([
        { name: 'Clients', link: userStore.isInternalUser ? '/clients' : undefined },
        { name: client.name, link: !userStore.isFacilityUser ? `/clients/${client.id}` : undefined },
        { name: 'Users', link: userStore.isExternalUser && client ? `/clients/${client.id}/users` : undefined },
      ]);

      setFacilityOptions(
        facilities.map((facility) => {
          return {
            label: getFacilityLabel(
              { ...facility, address_region: globalStore.getRegionById(facility.address_region_id) },
              true
            ),
            value: facility.id,
          };
        })
      );
    } catch (err) {
      globalStore.handleApiError(err as ApiError, toastMessages.fetchClient.error);
    }
  };

  const createUser = async () => {
    try {
      setIsCreating(true);

      // Validate
      const errors: Record<string, string> = {};
      if (!formValues.name) errors['name'] = 'Name is required';
      else if (formValues.name.length > 50) errors['name'] = 'Name cannot be more than 50 characters';
      if (!formValues.email) errors['email'] = 'Email is required';
      else if (!isValidEmail(formValues.email)) errors['email'] = 'Must be a valid email address';
      else if (formValues.email.length > 100) errors['email'] = 'Email cannot be more than 100 characters';
      if (formValues.phone && formValues.phone.length > 50)
        errors['phone'] = 'Phone number cannot be more than 50 characters';
      if (!formValues.role_id) errors['role_id'] = 'Role is required';
      if (
        ExternalRoles.includes(formValues.role_id) &&
        (!formValues.client_ids || formValues.client_ids.length === 0 || !formValues.client_ids[0])
      )
        errors['client_ids'] = 'Client is required';
      if (formValues.role_id === RoleTypes.FacilityAdmin && !formValues.facility_ids.length)
        errors['facility_ids'] = 'Must select at least one Facility';
      if (Object.keys(errors).length) {
        setFormErrors(errors);
        toast.error(toastMessages.formValidation.requiredFields);
        return;
      }

      const newUser: TCreateUserRequest = {
        name: formValues.name,
        email: formValues.email,
        phone: formValues.phone || null,
        role_id: formValues.role_id,
        is_email_verified: formValues.is_email_verified,
      };

      if (ExternalRoles.includes(formValues.role_id) && formValues.client_ids.length > 0) {
        newUser.client_ids = formValues.client_ids.filter((id) => !isNaN(id as number));
      }
      if (formValues.role_id === RoleTypes.FacilityAdmin) newUser.facility_ids = formValues.facility_ids;

      const promises: Promise<any>[] = [UserService.create(newUser)]; // eslint-disable-line @typescript-eslint/no-explicit-any

      const [user] = await Promise.all(promises);

      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(toastMessages.createUser.success);
      navigate(`/users/${user.id}`);
    } catch (err) {
      const apiErrors: Record<string, string>[] = (err as ApiError).body?.errors || [];
      if (apiErrors.length > 0) {
        const errors: Record<string, string> = { ...formErrors };
        for (const apiError of apiErrors) {
          const { field, rule } = apiError as Record<string, string>;

          if (rule === 'unique') {
            if (field === 'email') {
              errors[field] = 'This email address is already in use';
            } else {
              errors[field] = `This ${field} is already in use`;
            }
          }
        }
        setFormErrors(errors);
      } else {
        globalStore.handleApiError(err as ApiError, toastMessages.createUser.error);
      }
    } finally {
      setIsCreating(false);
    }
  };

  /** Effects **/
  useEffect(() => {
    document.title = getPageTitle(config.client.basePageTitle, 'Add User');
  }, []);

  useEffect(() => {
    if (filteredRoleIdOptions && !formValues.role_id) {
      setFormValues({
        ...formValues,
        role_id: filteredRoleIdOptions[0]?.value,
      });
    }
  }, [filteredRoleIdOptions, formValues.role_id]);

  useEffect(() => {
    if (formValues.client_ids && formValues.client_ids[0]) {
      fetchPageData(formValues.client_ids[0]);
    } else {
      setBreadcrumbNavItems([{ name: 'Users', link: userStore.isInternalUser ? '/users' : undefined }]);
      setFacilityOptions([]);
    }
  }, [formValues.client_ids]);

  /** Render **/
  return (
    <FormPage
      title={t('user:addUser')}
      breadcrumbNav={<BreadcrumbNav previousPages={breadcrumbNavItems} currentPageName="Add User" />}
      isLoading={isLoading}
    >
      <FormCard
        title={t('user:userInformation')}
        icon={<CircleInformation width="24px" height="24px" color="brand" />}
        isLoading={isLoading || isCreating}
        onSubmit={createUser}
      >
        <Form>
          <Box direction={isMobile ? 'column' : 'row'} gap="1rem">
            <Box fill="horizontal" gap="1rem">
              <FormCardSection title={t('user:contactInformation')}>
                <Input
                  name="user_name"
                  label={t('common:name')}
                  maxLength={50}
                  value={formValues.name}
                  setValue={(value) => updateFormValue('name', value)}
                  error={formErrors['name']}
                  onSubmit={createUser}
                  required
                />
                <Input
                  name="user_email"
                  label={t('user:email')}
                  maxLength={100}
                  value={formValues.email}
                  setValue={(value) => updateFormValue('email', value)}
                  error={formErrors['email']}
                  onSubmit={createUser}
                  type="email"
                  required
                />
                <Input
                  name="user_phone"
                  label={t('user:phone')}
                  maxLength={50}
                  value={formValues.phone}
                  setValue={(value) => updateFormValue('phone', formatPhoneNumber(value))}
                  error={formErrors['phone']}
                  onSubmit={createUser}
                  placeholder={isPhoneFocused ? '(555) 555-5555' : ''}
                  onFocus={() => setIsPhoneFocused(true)}
                  onBlur={() => setIsPhoneFocused(false)}
                />
              </FormCardSection>
            </Box>
            {isMobile ? <Line margin="0.5rem" /> : <Line direction="vertical" margin="3rem" />}
            <Box fill="horizontal" gap="1rem">
              <FormCardSection title={t('user:userRole')}>
                <Select
                  name="role_id"
                  label={t('user:userRole')}
                  value={formValues.role_id}
                  setValue={(value) => updateFormValue('role_id', value)}
                  error={formErrors['role_id']}
                  options={filteredRoleIdOptions ?? []}
                  required
                />
                {ExternalRoles.includes(formValues.role_id) && (
                  <>
                    <Box direction="row" align="center" gap="small">
                      <Box flex="grow">
                        <ClientsSelect
                          selectedClientId={formValues.client_ids[0]}
                          setSelectedClientId={(value) => {
                            if (value) {
                              const updatedClientIds = [...formValues.client_ids];
                              updatedClientIds[0] = value;
                              updateFormValue('client_ids', updatedClientIds);
                            }
                          }}
                          error={formErrors['client_ids']}
                          disabled={userStore.isExternalUser}
                          required
                          excludeClientIds={formValues.client_ids.filter((id) => id !== formValues.client_ids[0])}
                        />
                      </Box>
                      {formValues.client_ids[0] && formValues.role_id !== RoleTypes.FacilityAdmin && (
                        <GrommetButton
                          icon={<Add size="medium" />}
                          onClick={() => {
                            const updatedClientIds = [...formValues.client_ids];
                            updatedClientIds.push(undefined as unknown as TClientId);
                            updateFormValue('client_ids', updatedClientIds);
                            setShowMultipleClients(true);
                          }}
                          plain
                          disabled={userStore.isExternalUser}
                          tip={tooltips.addAnotherCompany}
                        />
                      )}
                    </Box>
                    {showMultipleClients && (
                      <Box margin={{ top: '1rem' }} gap="1rem">
                        <FormCardSection title="Additional Companies">
                          {(formValues.client_ids.slice(1).length > 0 ? formValues.client_ids.slice(1) : [NaN]).map(
                            (clientId, index) => (
                              <Box key={`client-${index + 1}`} direction="row" align="center" gap="small">
                                <Box flex="grow">
                                  <ClientsSelect
                                    name={`selected_client_${index + 2}`}
                                    label={`Company ${index + 2}`}
                                    selectedClientId={clientId}
                                    setSelectedClientId={(value) => {
                                      if (value) {
                                        const updatedClientIds = [...formValues.client_ids];
                                        updatedClientIds[index + 1] = value;
                                        updateFormValue('client_ids', updatedClientIds);
                                      }
                                    }}
                                    error={formErrors['client_ids']}
                                    disabled={userStore.isExternalUser}
                                    excludeClientIds={formValues.client_ids.filter((id) => id !== clientId)}
                                  />
                                </Box>
                                <GrommetButton
                                  icon={<Close />}
                                  onClick={() => {
                                    const updatedClientIds = [...formValues.client_ids];
                                    updatedClientIds.splice(index + 1, 1);
                                    updateFormValue('client_ids', updatedClientIds);

                                    if (updatedClientIds.length <= 1) {
                                      setShowMultipleClients(false);
                                    }
                                  }}
                                  plain
                                  disabled={userStore.isExternalUser}
                                />
                              </Box>
                            )
                          )}
                        </FormCardSection>
                      </Box>
                    )}
                  </>
                )}
                {formValues.role_id === RoleTypes.FacilityAdmin && !!facilityOptions?.length && (
                  <Select
                    name="facility_ids"
                    multiple
                    label={t('common:facilities')}
                    values={formValues.facility_ids}
                    setValues={(value) => updateFormValue('facility_ids', value)}
                    options={facilityOptions}
                    error={formErrors['facility_ids']}
                    required
                  />
                )}
              </FormCardSection>
            </Box>
          </Box>
        </Form>
      </FormCard>
    </FormPage>
  );
});
